import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SigninRequest } from '@interfaces/auth.interface';

import { useLogoutMutation, useSigninMutation } from '@api/auth.api';
import api from '@api/index';

import {
  logout as logoutAction,
  selectCurrentUser,
  setCredentials,
} from '@slices/auth.slice';

const useAuth = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);

  const [signinMutation, { isLoading: isLoadingSignin }] = useSigninMutation();
  const [logoutMutation, { isLoading: isLoadingLogout }] = useLogoutMutation();

  const signin = useCallback(
    async (data: SigninRequest) => {
      const user = await signinMutation(data).unwrap();

      dispatch(setCredentials({ user }));
    },
    [dispatch, signinMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation().unwrap();

    dispatch(api.util.resetApiState());
    dispatch(logoutAction());
  }, [dispatch, logoutMutation]);

  return useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      signin,
      logout,
      isLoading: isLoadingSignin,
      isLoadingLogout,
    }),
    [user, signin, logout, isLoadingSignin, isLoadingLogout],
  );
};

export default useAuth;
